import type { UDocClient, UDocViewer } from '@docmentis/udoc-viewer';
import { useEffect, useRef, useState } from 'react';

import { toError, useArrayBuffer } from './loaders';
import { PreviewError, PreviewStatus } from './preview-state';

export function PptxArtifactPreview({ sourceUrl, title }: { sourceUrl: string; title: string }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { data, error, loading } = useArrayBuffer(sourceUrl);
  const [viewerState, setViewerState] = useState<{ error: Error | undefined; loading: boolean }>({
    error: undefined,
    loading: true,
  });

  useEffect(() => {
    if (!data || !containerRef.current) return undefined;

    let cancelled = false;
    let client: UDocClient | undefined;
    let viewer: UDocViewer | undefined;
    let unsubscribeError: (() => void) | undefined;
    const buffer = data;
    const container = containerRef.current;

    async function renderPptx() {
      setViewerState({ error: undefined, loading: true });
      container.innerHTML = '';

      try {
        const { UDocClient } = await import('@docmentis/udoc-viewer');
        client = await UDocClient.create({ disableUpdateCheck: true });
        if (cancelled) return;

        viewer = await client.createViewer({
          activePanel: null,
          container,
          disableComments: true,
          disableFullscreen: true,
          disableLeftPanel: true,
          disableRightPanel: true,
          disableSearch: true,
          hideToolbar: true,
          scrollMode: 'continuous',
          theme: 'light',
          zoomMode: 'fit-spread-width',
        });
        unsubscribeError = viewer.on('error', ({ error: viewerError }) => {
          setViewerState({ error: viewerError, loading: false });
        });

        await viewer.load(new Uint8Array(buffer));
        if (!cancelled) {
          setViewerState({ error: undefined, loading: false });
        }
      } catch (caughtError) {
        if (!cancelled) {
          setViewerState({ error: toError(caughtError), loading: false });
        }
      }
    }

    void renderPptx();

    return () => {
      cancelled = true;
      unsubscribeError?.();
      viewer?.destroy();
      client?.destroy();
      container.innerHTML = '';
    };
  }, [data]);

  if (loading) return <PreviewStatus>Loading {title}...</PreviewStatus>;
  if (error) return <PreviewError message={error.message} />;
  if (viewerState.error) return <PreviewError message={viewerState.error.message} />;

  return (
    <div className="relative h-full min-h-[480px] bg-slate-100">
      {viewerState.loading && (
        <div className="absolute inset-0 z-10 bg-slate-100">
          <PreviewStatus>Loading {title}...</PreviewStatus>
        </div>
      )}
      <div ref={containerRef} className="h-full min-h-[480px]" />
    </div>
  );
}
